/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/BackgroundData/components/BackgroundGeographicalSpecificityField.tsx.
 */

import React from "react";
import type { DropdownOption } from "../../../../../../interfaces/dropdownInterfaces";
import BackgroundFieldLabel from "./BackgroundFieldLabel";
import {
  type BackgroundGeoSpec,
  formatBackgroundGeoDisplayLabel,
  normalizeBackgroundGeoSpec,
  resolveGeographicScopeLabel,
} from "../../../../../../utils/backgroundGeographicalSpecificity";

type Props = {
  geoSpec: BackgroundGeoSpec | string;
  geoSelection: string;
  stateOptions: DropdownOption[];
  geographicScopeOptions?: DropdownOption[];
  geographicScopeLoading?: boolean;
  onGeoSpecChange?: (spec: BackgroundGeoSpec) => void;
  onGeoSelectionChange?: (value: string) => void;
  readOnly?: boolean;
  error?: string;
  /** Match Data Review / Data Acquisition half-width cells */
  cellClassName?: string;
};

const BackgroundGeographicalSpecificityField: React.FC<Props> = ({
  geoSpec: geoSpecRaw,
  geoSelection,
  stateOptions,
  geographicScopeOptions = [],
  geographicScopeLoading = false,
  onGeoSpecChange,
  onGeoSelectionChange,
  readOnly = false,
  error,
  cellClassName = "field-row",
}) => {
  const geoSpec = normalizeBackgroundGeoSpec(geoSpecRaw);
  const selectedGlobalScope = String(geoSelection ?? "").trim();
  const globalScopeOptions = geographicScopeOptions;
  const globalScopeInList = globalScopeOptions.some(
    (o) => o.value === selectedGlobalScope
  );
  const mergedGlobalScopeOptions =
    selectedGlobalScope && !globalScopeInList
      ? [
          {
            id: -1,
            label: resolveGeographicScopeLabel(
              selectedGlobalScope,
              globalScopeOptions
            ),
            value: selectedGlobalScope,
          },
          ...globalScopeOptions,
        ]
      : globalScopeOptions;

  const rootClass = [
    cellClassName,
    "background-geo-specificity-row",
    error ? "field-has-error" : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (readOnly) {
    return (
      <div className={rootClass}>
        <BackgroundFieldLabel
          label="Geographical Specificity"
          isMandatory={false}
        />
        <input
          type="text"
          readOnly
          className="readonly-input"
          value={formatBackgroundGeoDisplayLabel(
            geoSpec,
            geoSelection,
            stateOptions,
            geographicScopeOptions
          )}
        />
      </div>
    );
  }

  return (
    <div className={rootClass}>
      <BackgroundFieldLabel label="Geographical Specificity" isMandatory />
      <div className="geo-btn-row">
        <button
          type="button"
          className={geoSpec === "global" ? "geo-btn-active" : "geo-btn"}
          onClick={() => onGeoSpecChange?.("global")}
        >
          Global
        </button>
        <button
          type="button"
          className={geoSpec === "india" ? "geo-btn-active" : "geo-btn"}
          onClick={() => onGeoSpecChange?.("india")}
        >
          India
        </button>
        <button
          type="button"
          className={
            geoSpec === "state"
              ? "geo-btn-active geo-btn--state"
              : "geo-btn geo-btn--state"
          }
          onClick={() => onGeoSpecChange?.("state")}
        >
          State Specific
        </button>
      </div>
      {geoSpec === "global" && (
        <select
          className="background-geo-select"
          value={selectedGlobalScope}
          onChange={(event) => onGeoSelectionChange?.(event.target.value)}
          disabled={geographicScopeLoading}
          aria-label="Geographic Scope"
        >
          {geographicScopeLoading ? (
            <option value="">Loading geographic scopes…</option>
          ) : mergedGlobalScopeOptions.length === 0 ? (
            <option value="">No geographic scopes available</option>
          ) : (
            mergedGlobalScopeOptions.map((opt) => (
              <option key={opt.id ?? opt.value} value={opt.value}>
                {opt.label ?? opt.value}
              </option>
            ))
          )}
        </select>
      )}
      {geoSpec === "state" && (
        <select
          className="background-geo-select"
          value={geoSelection}
          onChange={(event) => onGeoSelectionChange?.(event.target.value)}
          disabled={!stateOptions.length}
        >
          <option value="">Select state</option>
          {stateOptions.map((opt) => (
            <option key={opt.id ?? opt.value} value={opt.value}>
              {opt.label ?? opt.value}
            </option>
          ))}
        </select>
      )}
      {error ? <span className="error-message">{error}</span> : null}
    </div>
  );
};

/** React component — Background Geographical Specificity Field. */
export default BackgroundGeographicalSpecificityField;
